import MailOutlined from '@ant-design/icons/lib/icons/MailOutlined'
import PhoneOutlined from '@ant-design/icons/lib/icons/PhoneOutlined'
import {Button, Modal} from 'antd'
import {organization_getById_Api} from 'apis'
import {organizationSchema} from 'apis/schema'
import AppTextLogoSvg from 'appConfigs/svgs/AppTextLogoSvg'
import Loading from 'components/Loading'
import {NullAsDefault} from "views/Shared";
import Toggle from 'components/Toggle'
import {CONTACT_EMAIL, CONTACT_PHONE,} from 'envs/_current/config'
import _ from 'lodash'
import useAsync from 'modules/asyncCache/useAsync'
import Translate from 'modules/local/Translate'
import useTranslate from 'modules/local/useTranslate'
import {useHistory} from 'modules/navigation/useRouter'
import {permissionCodes} from 'modules/permissions/contants'
import PermissionProvider from 'modules/permissions/PermissionProvider'
import React, {useEffect, useRef} from 'react'
import {useSelector} from 'react-redux'
import {useRouteMatch} from 'react-router-dom'
import {getLogin} from 'redux/auth/selectors'
import {useSelectEntities} from 'redux/entities/useSelectEntities'
import ServerContainer from '../Server/ServerContainer'
import {OrganizationContext} from './OrganizationContext'
import {routes} from './routes'
import {RiShareForwardFill} from "react-icons/ri";
import {RxPlus} from "react-icons/rx";
import {PiGearSix} from "react-icons/pi";

const Error404 = ({...props}) => {
    const {t} = props
    return (
        <div className=" px-3 py-16 flex flex-col absolute inset-0 w-full h-full justify-center items-center">
            <div style={{fontSize: '9rem'}}>
                <AppTextLogoSvg size={16}/>
            </div>
            <div className=" p-6 flex justify-center items-center font-bold text-2xl">
                {t('page not found')}
            </div>
            <div className=" p-3 flex justify-center items-center text-color-300 text-center">
                {t(
                    'The page you are opening does not exist, please check the link or return to the homepage'
                )}
            </div>
            <Button
                onClick={() => {
                    window.location.href = '/'
                }}
                className="button-rounded-lg no-border"
                type="primary">
                {t('home')}
            </Button>
        </div>
    )
}

const Error401 = ({...props}) => {
    const {t, history, login} = props

    return (
        <div className="px-6 flex flex-col space-y-3 absolute inset-0 w-full h-full justify-center items-center">
            <div style={{fontSize: '9rem'}}>
                <AppTextLogoSvg size={16}/>
            </div>
            <div className="p-3 flex justify-center items-center font-bold text-3xl">
                {t('page not found')}
            </div>
            <div className="p-3 flex justify-center items-center text-color-400 text-center text-lg italic font-light">
                {t(
                    'the page does not exist or the page is for internal use, you must be a member to view the content of this page.'
                )}
            </div>
            <div className="flex flex-center gap-3">
                <Button
                    onClick={() => {
                        window.location.href = '/'
                    }}
                    className="button-rounded-lg">
                    {t('return home')}
                </Button>
                {!!!login && (
                    <Button
                        onClick={() => {
                            history.push('/login')
                        }}
                        className="button-rounded-lg no-border"
                        type="primary">
                        <div className="px-6">{t('login')}</div>
                    </Button>
                )}
            </div>
        </div>
    )
}

const Error403 = ({...props}) => {
    const {t} = props

    return (
        <div className="px-6 flex flex-col space-y-6 absolute inset-0 w-full h-full justify-center items-center">
            <div style={{fontSize: '9rem'}}>
                <AppTextLogoSvg size={16}/>
            </div>
            <div className="p-3 flex justify-center items-center font-bold text-3xl">
                {t('page not found')}
            </div>
            <div className="p-3 flex justify-center items-center text-color-400 text-center text-lg italic font-light">
                {t(
                    'the page does not exist or the page is for internal use, you must be a member to view the content of this page.'
                )}
            </div>
            <div className="flex flex-center gap-3">
                <Button
                    onClick={() => {
                        window.location.href = '/'
                    }}
                    className="button-rounded-lg no-border">
                    {t('return home')}
                </Button>
                <Toggle>
                    {(isToggle, toggle) => (
                        <React.Fragment>
                            <Button
                                onClick={toggle}
                                className="button-rounded-lg no-border px-6"
                                type="primary">
                                {t('contact')}
                            </Button>
                            <Modal
                                title={
                                    <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                                        {t('contact')}
                                    </div>
                                }
                                maskClosable
                                className="custom-modal"
                                destroyOnHidden
                                open={isToggle}
                                onCancel={toggle}
                                onOk={toggle}
                                cancelButtonProps={{hidden: true}}>
                                <div className="py-6">
                                    {t('Please contact the administrator')}
                                </div>
                                <div className="flex flex-1 items-center space-x-3">
                                    <MailOutlined/>
                                    <p>{CONTACT_EMAIL}</p>
                                </div>
                                <div className="flex flex-1 items-center space-x-3">
                                    <PhoneOutlined/>
                                    <p>{CONTACT_PHONE}</p>
                                </div>
                            </Modal>
                        </React.Fragment>
                    )}
                </Toggle>
            </div>
        </div>
    )
}

const showModal = ({title, status, params = {}}) => {
    let render = NullAsDefault
    switch (status) {
        case 401:
            render = Error401
            break
        case 403:
            render = Error403
            break
        case 404:
        case 500:
            render = Error404
            break
    }

    Modal.info({
        bodyStyle: {minHeight: '60vh'},
        width: 720,
        className: 'custom-modal',
        // maskClosable: true,
        footer: null,
        okButtonProps: {hidden: true},
        cancelButtonProps: {hidden: true},
        title: title,
        content: render({
            ...params,
        }),
    })
}

const OrganizationProvider = ({id, children}) => {
    let {url} = useRouteMatch()
    const history = useHistory()
    const t = useTranslate()
    const login = useSelector(getLogin)
    const item = useSelectEntities(id, organizationSchema)

    const menus = item?.edit ? [
        ['share', 'share', <RiShareForwardFill size={12} className="text-color-400"/>],
        ['createChannel', 'create channel', <RxPlus size={13} className="text-color-400"/>],
        ['createEvent', 'create event', <RxPlus size={13} className="text-color-400"/>],
        ['divider'],
        ['setting', 'general settings', <PiGearSix size={12} className="text-color-400"/>],
    ] : [
        ['share', 'share', <RiShareForwardFill size={12} className="text-color-400"/>]
    ]

    const e = useRef(null)
    const newE = item && (
        <PermissionProvider
            permissions={{
                [permissionCodes.canInvite]: Number(
                    item.privacy === 1 || item.edit || item.join_status === 1
                ),
                [permissionCodes.canEdit]: Number(item.edit),
                ...(item?.available_actions || {})
            }}>
            <ServerContainer
                {...{
                    item,
                    schema: organizationSchema,
                    id: item.id,
                    type: item._type,
                    routes: routes,
                    createMenuContext: ({setCurrent, handleActionClick}) =>
                        item && ({
                            className: 'background',
                            items: menus.map(([key, label, icon], i) =>
                                    key === 'divider'
                                        ? {
                                            type: 'divider',
                                            key: `divider-${i}`,
                                        }
                                        : {
                                            key,
                                            icon,
                                            label: (
                                                <Translate>
                                                    {(t) => (
                                                        <span className="font-medium text-sm text-color-100">{t(label)}</span>
                                                    )}
                                                </Translate>
                                            ),
                                        }
                            ),
                            onClick: ({key}) => {
                                handleActionClick(key);
                            },
                        }),
                }}>
                {children}
            </ServerContainer>
        </PermissionProvider>
    )
    if (newE && newE !== null) {
        e.current = newE
    }

    const data = useAsync({
        apiInfo: organization_getById_Api,
        query: {
            ':id': id,
        },
    })

    const {error, response, isLoading = false} = data

    useEffect(() => {
        if (error && !isLoading && !!response) {
            const status = _.get(response, 'status')
            if (status) {
                showModal({
                    status,
                    title: t(status),
                    params: {
                        t,
                        login,
                        history,
                    },
                })
            }
        }
    }, [isLoading, response, error])

    return (
        <OrganizationContext.Provider
            value={{
                id,
                url,
                isLoading,
                organization: item,
                isSucceed: Number(_.get(response, 'status')) === 200,
            }}>
            {e.current || (
                <div className=" p-3 flex flex-col absolute inset-0 w-full h-full justify-center items-center">
                    <Loading/>
                </div>
            )}
        </OrganizationContext.Provider>
    )
}

export default OrganizationProvider
